import { ApiError } from './errors';

/**
 * Reading an RFC 7807 problem for WHAT WENT WRONG, never for the status.
 *
 * The repo rule is to branch on `problem.code`, and the reason is that one
 * status covers opposite causes: `/admin/dispute-appeals` answers 403 both when
 * the operator lacks a capability (fix: grant it) and when their session has no
 * MFA (fix: log in again with the second factor), and a 409 on a milestone is
 * either a unique-constraint collision or a milestone the community already
 * reached — which is not an error the operator can undo.
 *
 * Where the backend gives a domain `code`, that is what these read. Where it
 * does not — and on several of these paths it does not, see the notes on each —
 * they fall back to `type`, which is a stable URI, and only then to a distinctive
 * `detail`. The status is never the discriminator.
 */
export interface Problem {
  code?: string;
  type?: string;
  title?: string;
  detail?: string;
  /** What would make the request pass. The staff guard sends this. */
  remedy?: string;
}

/** The problem body of an `ApiError`, or null when this is not one. */
export function problemOf(e: unknown): Problem | null {
  if (!(e instanceof ApiError)) return null;
  const body = e.details;
  const p: Problem = { code: e.code };
  if (body && typeof body === 'object') {
    const b = body as Record<string, unknown>;
    if (typeof b.type === 'string') p.type = b.type;
    if (typeof b.title === 'string') p.title = b.title;
    if (typeof b.detail === 'string') p.detail = b.detail;
    if (typeof b.remedy === 'string') p.remedy = b.remedy;
  }
  return p;
}

/**
 * The domain `code` the backend attached, or null when there is none.
 *
 * `http.ts` writes `HTTP_ERROR` into `ApiError.code` when the body carries no
 * code at all, and that placeholder is filtered out here: branching on it would
 * be branching on "the backend said nothing", which every codeless error of
 * every status shares. A caller that gets null knows it has to fall back to
 * `type` or `detail` rather than believing it recognised something.
 */
export function problemCode(e: unknown): string | null {
  const code = problemOf(e)?.code;
  return code && code !== 'HTTP_ERROR' ? code : null;
}

/**
 * The URI the staff-capability guard stamps on its 403. It has no `code`, so
 * this is the stable identifier available — and it is a real one, unlike the
 * status, which the MFA guard also answers with.
 */
const CAPABILITY_DENIED_TYPE = 'https://errors.kocollectibles.app/staff/capability-denied';

/**
 * The capability the caller is missing, or null when this is not that 403.
 *
 * Returns the NAME because the guard puts it in `detail` (`Missing capability:
 * MANAGE_USERS`), and a screen that can name the permission is a screen whose
 * operator knows what to ask for. An empty string means "it is this 403, but
 * the name could not be read" — still a different situation from `null`.
 */
export function missingCapability(e: unknown): string | null {
  const p = problemOf(e);
  if (!p || p.type !== CAPABILITY_DENIED_TYPE) return null;
  const m = /Missing capability:\s*([A-Z_]+)/.exec(p.detail ?? '');
  return m ? m[1] : '';
}

/**
 * The other 403: the session is authenticated but not MFA-verified. Thrown as a
 * bare `ForbiddenException('MFA required')`, so `detail` is all there is.
 */
export function isMfaRequired(e: unknown): boolean {
  const p = problemOf(e);
  return !!p && p.detail === 'MFA required';
}

/**
 * The feature key an admin has switched off, or null when the 503 is not the
 * kill switch. `RequireFeatureGuard` throws a bare `HttpException` whose message
 * names the flag, so the name is recoverable even though there is no code.
 *
 * Distinguishing matters: a kill-switch 503 is somebody's deliberate decision
 * and the screen should say which flag; any other 503 is the service being
 * down, and telling the operator to check a flag would send them hunting for a
 * switch that is not the problem.
 */
export function disabledFeature(e: unknown): string | null {
  const p = problemOf(e);
  const m = /Feature "([^"]+)" is currently disabled/.exec(p?.detail ?? '');
  return m ? m[1] : null;
}

/**
 * A raw Prisma unique-constraint violation.
 *
 * `P2002` is the whole of what the backend says here: `ProblemExceptionFilter`
 * keeps the Prisma code but drops `meta.target`, which is the half that would
 * name the colliding column. So this separates "something unique collided" from
 * the domain 409s on the same routes, and no further. Callers that need to know
 * WHICH column have to reason from what they sent — see the milestone dialogs.
 */
export function isUniqueConflict(e: unknown): boolean {
  return problemOf(e)?.code === 'P2002';
}
